import { useLingui } from '@lingui/react/macro'
import type { ReactNode } from 'react'
import { COUNTER_LOCALE_ATTR, COUNTER_VALUE_ATTR } from '@/lib/counter-contract'
import { formatNumber } from '@/lib/utils'
import type { Locale } from 'shared'

/**
 * One rolling digit: a strip behind a one-figure window, EMPTY cell first (./README.md).
 *
 * That empty cell is how a place the count has not reached shows nothing, and it is not a trick: the strip
 * counts up from it, so the blank rolls away exactly as the count passes `10^p`. `cells` is how far down the
 * strip the column travels, its true `value / 10^p` where that can be rendered at all.
 */
function Digit(props: { cells: number; strip: ReactNode[]; glyph: string; ease: string }) {
  return (
    <span className="digit-roll-window relative inline-block overflow-hidden">
      {/* An invisible copy of THIS digit, in normal flow, there only to size the window: the column is
          absolute and so cannot size its own parent. The real digit rather than a fixed `0` so the slot takes
          the width of the glyph actually shown once the count has settled. */}
      <span aria-hidden className="invisible block">
        {props.glyph}
      </span>
      {/* Stretched to the window (`inset-x-0`) and centring its cells, rather than half-width-offset with a
          `-50%`: the figure is `tabular-nums`, so a cell is exactly the window's width and there is nothing to
          offset — and a percentage of a fractional width rounds differently at each zoom level, which is what
          slid the digits sideways. The travel is counted in `lh`, one cell, so it lands dead on the strip. */}
      <span
        aria-hidden
        style={{ '--cell': props.cells, '--ease': props.ease }}
        className="digit-roll absolute inset-x-0 top-0 flex flex-col items-center"
      >
        {props.strip.map((cell, i) => (
          // oxlint-disable-next-line react/no-array-index-key -- a strip position, which is its identity
          <span key={i}>{cell}</span>
        ))}
      </span>
    </span>
  )
}

/**
 * A figure as an ODOMETER: one rolling 0–9 column per digit, the locale's separators standing still between
 * them. Rendered identically on both sides — the columns are static markup and the roll is CSS — so there is
 * no hydration state to get wrong and nothing waits for the bundle.
 *
 * The real figure reaches a screen reader once from the `sr-only` copy; the columns are `aria-hidden`
 * throughout and contribute no text.
 */
export function RollingFigure(props: { value: number; label: string; startMs?: number }) {
  const { i18n } = useLingui()
  const locale = i18n.locale as Locale
  const figure = formatNumber(locale, props.value, 0)
  const startMs = props.startMs ?? 0
  const cells = toCells(figure, props.value)
  const settleMs = countMs(props.value)

  return (
    <div style={{ '--start': `${startMs}ms` }} className="flex items-baseline gap-3.5">
      <span
        // One count means one clock for the whole figure; only the distance each column runs along it,
        // and so its own `--ease`, differs.
        style={{ '--settle': `${settleMs}ms` }}
        // `leading-none` is load-bearing: a line box with leading in it puts AIR either side of the glyph,
        // and a window one line box tall then spends the roll showing that air rather than the digits.
        className="text-3xl leading-none font-semibold tracking-tight tabular-nums"
        // A page translator treats a figure as prose and restates it with another locale's separators.
        translate="no"
        {...{ [COUNTER_VALUE_ATTR]: props.value, [COUNTER_LOCALE_ATTR]: locale }}
      >
        <span className="sr-only">{figure}</span>
        {/* A FLEX row, not inline-blocks on a baseline: a window that clips takes its baseline from its bottom
            edge, so on a baseline the digits would sit lower than the separators between them. */}
        <span aria-hidden className="inline-flex">
          {cells.map((cell) => (
            <Digit key={cell.id} cells={cell.cells} strip={cell.strip} glyph={cell.glyph} ease={cell.ease} />
          ))}
        </span>
      </span>
      {/* The one thing still FADED, because it is prose and cannot roll in from anywhere. It waits for its
          figure: a second counter that takes its turn later would otherwise sit as a caption beside a blank
          space for as long as the first one runs. */}
      <span
        style={{ '--reveal': `${startMs}ms`, '--fade': `${LABEL_FADE_MS}ms` }}
        className="digit-reveal text-muted-foreground text-sm"
      >
        {props.label}
      </span>
    </div>
  )
}

export interface Cell {
  id: string
  /** The strip behind the window, its first cell EMPTY: what a place shows before the count reaches it. */
  strip: ReactNode[]
  /** How far down that strip this column travels, in cells. */
  cells: number
  /** The glyph this column ends on, which sizes its window — a lapping strip does not end on it. */
  glyph: string
  ease: string
}

/**
 * What one PLACE of a figure is worth in milliseconds, and so how long a whole count takes.
 *
 * A duration cannot be a flat number here: the two figures on the hero have seven places and five, so one flat
 * clock made the shorter one count half again as slowly as the longer — 252 ms a place against 177 — and it
 * visibly dragged. Pricing the PLACE instead makes both climb at one pace and the shorter figure simply take
 * less time, which is what a counter does.
 */
const PLACE_MS = 140

/** How long the count of this figure runs. One clock for every column of it, because it is one number. */
function countMs(value: number): number {
  return Math.round(PLACE_MS * countSpan(value))
}

/**
 * How the count is WALKED: `progress ** COUNT_EASE`, so under one it eases OUT and over one it eases in.
 *
 * At a constant rate the logistic below gives one even cadence end to end, which is too level to watch. Easing
 * the walk out bends it into a deceleration: the places appear 81, 112, 133, 150, 165 and 177 ms apart and
 * then settle 201, 211, 219, 227, 235 and 243 ms apart. Those last figures are the ones this exponent is set
 * by — a harder ease-out leaves the units turning alone for half a second at the end, with nothing beside it.
 */
const COUNT_EASE = 0.72
/** How long a LABEL takes to fade in beside its figure. Nothing else on the counters fades. */
const LABEL_FADE_MS = 280

/** The empty cell every strip begins with, and what a separator rolls in from. */
const BLANK = ''

/**
 * The most turns a place may make and still be followed by eye, and so be rendered honestly.
 *
 * Under this a column turns its true `value / 10^p` times — for the leading digit of this figure, two: nought,
 * one, two. Over it no strip could hold the truth and no eye could check it, so the column laps one strip
 * instead. The boundary falls exactly where reading stops, which is why it cannot be seen.
 */
const READABLE_TURNS = 40
/** Laps a blurred column makes. Enough to read as a blur, few enough to describe in one `linear()`. */
const BLUR_TURNS = 16

/**
 * The figure as ONE odometer counting up from zero, written out per place (./README.md).
 *
 * Nothing here is an interval picked to look right. A place holds `value / 10^p`: it ticks over that many
 * times, it comes into existence the moment the count first reaches `10^p`, and it stops changing once the
 * count is within half of its own place. All three fall out of `count`, which is why the digits arrive one by
 * one and then settle one by one, leftmost first, on the one cadence that curve has.
 */
export function toCells(figure: string, value: number): Cell[] {
  const characters = figure.split('')
  const total = characters.filter((character) => /\d/.test(character)).length
  const curve = fullCurve(value)
  let place = total
  let leftPlace = total
  return characters.map((character, index) => {
    const id = `${index}-${character}`
    // A separator belongs to the digit on its LEFT, the one whose place it opens, and rolls in on the same
    // stretch of the count: the figure reads `477`, then `7.477`, never a dot over a number without one.
    if (!/\d/.test(character)) {
      const ease = openEasing(leftPlace, value, curve)
      return { id, strip: [BLANK, character], cells: 1, glyph: character, ease }
    }
    place -= 1
    leftPlace = place
    const digit = Number(character)
    const exact = value / 10 ** place
    const turns = Math.floor(exact)
    // The true travel overshoots the digit it lands on — 2.94 cells, not 2 — so the column reaches its digit
    // early and holds, which IS the place settling. `lead` is that overshoot, and clamping it is the settle.
    const lead = turns > 0 ? exact / turns : 1
    if (turns <= READABLE_TURNS) {
      // From the blank straight to its ONE: a place is not shown holding a nought it has already left, and
      // this way the blank rolls off at exactly `value / 10^p == 1`, the moment the place comes into being.
      const strip = [BLANK, ...Array.from({ length: turns }, (_, i) => String((i + 1) % 10))]
      return { id, strip, cells: turns, glyph: character, ease: rollEasing(lead, curve) }
    }
    // Every lapping column would otherwise be on the same strip at the same moment and show the SAME digit,
    // seven of them in step, which no counter has ever done. They start a different way round instead.
    const offset = (place * 3) % 10
    const strip = [BLANK, ...Array.from({ length: LAP_CELLS }, (_, i) => String((offset + i) % 10))]
    const ease = lapEasing((digit - offset + 10) % 10, exact, lead, curve, exact)
    return { id, strip, cells: LAP_CELLS, glyph: character, ease }
  })
}

/**
 * A separator: it has one cell to travel and must be over it exactly as the place on its left opens.
 *
 * The stretch it rolls over is that place's own — from halfway to its one, to its one — so the dot arrives
 * with the digit beside it rather than on a timer of its own.
 */
function openEasing(place: number, value: number, curve: Curve): string {
  const from = curve.at((0.5 * 10 ** place) / value)
  const to = curve.at(10 ** place / value)
  return `linear(0 0%,0 ${(from * 100).toFixed(3)}%,1 ${(to * 100).toFixed(3)}%,1 100%)`
}

/**
 * The count, as a logistic in LOG space: `log(value)` climbs evenly while the figure is small and
 * `log(remaining)` falls evenly while it is nearly done.
 *
 * That is what makes both ends of the animation legible on ONE cadence — a place appearing, then a place
 * settling, leftmost first — and `COUNT_EASE` is what then bends that cadence into a deceleration. `span` is
 * not tuned: it is fixed by wanting the count to start below one and end within half of the figure.
 */
function countSpan(value: number): number {
  return 2 * Math.log10(2 * Math.max(value, 1))
}

/** A count, both ways round: where it stands `progress` of the way through, and when it stands somewhere. */
interface Curve {
  share: (progress: number) => number
  at: (share: number) => number
}

/** The full count of a figure, from nothing. */
function fullCurve(value: number): Curve {
  const span = countSpan(value)
  return {
    share: (progress) => 1 / (1 + 10 ** (-span * (walk(progress) - 0.5))),
    at: (share) => {
      const clamped = Math.min(Math.max(share, 1e-12), 1 - 1e-12)
      const walked = 0.5 + Math.log10(clamped / (1 - clamped)) / span
      return walked <= 0 ? 0 : Math.min(walked, 1) ** (1 / COUNT_EASE)
    },
  }
}

/** How far along the count the animation has WALKED at `progress` of its length. */
function walk(progress: number): number {
  return progress ** COUNT_EASE
}

/**
 * A place slow enough to render: it simply follows the count, `lead` times over, and holds once it arrives.
 *
 * The hold is not a pause bolted on — it is this place having stopped changing while the count carries on in
 * the places to its right, which is exactly why the figure settles from the left.
 */
function rollEasing(lead: number, curve: Curve): string {
  const samples = Array.from({ length: SAMPLES - 1 }, (_, i) =>
    Math.min(1, lead * curve.share((i + 1) / SAMPLES)).toFixed(5),
  )
  return `linear(0,${samples.join(',')},1)`
}

/**
 * A place too fast to render: one strip lapped over and over, as a SAWTOOTH.
 *
 * Each lap ramps the strip through its ten cells and resets, which is invisible because cell ten is cell
 * nought. The laps are placed by the count itself, so a blurred column speeds up and slows down with the rest
 * of the figure rather than on a clock of its own, and the last part-lap sets it down on its digit — `lands`
 * cells on from wherever its strip began. The first lap starts one cell further back, at the blank.
 */
function lapEasing(lands: number, exact: number, lead: number, curve: Curve, opensAt?: number): string {
  const laps = BLUR_TURNS + lands / 10
  const at = (lapped: number) => `${(curve.at(lapped / (laps * lead)) * 100).toFixed(3)}%`
  // A lap resets to the strip's FIRST digit, never to its blank: the blank is only crossed on the way in, and
  // it is crossed when the count reaches this place — not when the first lap happens to end, which is the same
  // instant for every blurred column and had all of them arriving together. A resumed count has no blank.
  const first = (1 / LAP_CELLS).toFixed(4)
  const opens = (share: number) => `${(curve.at(share / (opensAt ?? exact)) * 100).toFixed(3)}%`
  const stops = opensAt === undefined ? [`${first} 0%`] : ['0 0%', `0 ${opens(0.5)}`, `${first} ${opens(1)}`]
  for (let lap = 1; lap <= BLUR_TURNS; lap += 1) stops.push(`1 ${at(lap)}`, `${first} ${at(lap)}`)
  const rest = ((1 + lands) / LAP_CELLS).toFixed(4)
  return `linear(${stops.join(',')},${rest} ${at(laps)},${rest} 100%)`
}

/** Cells a lapping strip holds after its blank: ten digits, then the first again so the reset cannot be seen. */
const LAP_CELLS = 11

/** Points a curve is handed to the browser as. */
const SAMPLES = 48
